<template>
  <AuthLayout :mode="mode">
    <AppCard :mode="mode" title="Edit Listing">
      <template #body>
        <div class="mt-6">
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppSelect
              :mode="mode"
              title="Manufacturer"
              v-model="listing.mfr_id"
            >
              <template #options>
                <option
                  v-for="(mfr, key) in mfrs"
                  :key="key"
                  :value="mfr.mfr.mfr_id"
                >
                  {{ mfr.mfr.mfr }}
                </option>
              </template>
            </AppSelect>
            <AppSelect :mode="mode" title="Buy/Sell" v-model="listing.buy_sell">
              <template #options>
                <option selected value="B">Buy</option>
                <option value="S">Sell</option>
              </template>
            </AppSelect>
            <AppSelect :mode="mode" title="Country" v-model="listing.location">
              <template #options>
                <option value="">Country</option>
                <option
                  v-for="(country, key) in countries"
                  :value="country.code"
                  :key="key"
                >
                  {{ country.country }}
                </option>
              </template>
            </AppSelect>
            <AppInput
              :mode="mode"
              id="date"
              title="Date available/needed by"
              v-model="listing.need_by"
              type="date"
            />
            <div class="col-span-2 sm:col-span-1">
              <AppCheckbox
                :mode="mode"
                id="researching"
                title="Researching (Not ready to buy/sell)"
                v-model="listing.researching"
              />
            </div>
          </div>
          <div class="mt-10 md:mt-8">
            <div class="grid gap-2">
              <div class="grid gap-4">
                <div
                  v-for="(item, index) in listing.items"
                  class="flex gap-3 md:grid md:gap-4"
                  :key="index"
                >
                  <div class="flex-1">
                    <div class="grid grid-cols-3 gap-3 md:grid-cols-1 md:gap-4">
                      <div>
                        <AppInput
                          :mode="mode"
                          v-model="item.machine"
                          placeholder="Machine or part number"
                          :hasLabel="false"
                        />
                      </div>
                      <div>
                        <AppInput
                          :mode="mode"
                          v-model="item.model"
                          placeholder="Model or description"
                          :hasLabel="false"
                        />
                      </div>
                      <div>
                        <AppInput
                          :mode="mode"
                          v-model="item.comments"
                          placeholder="Comment"
                          :hasLabel="false"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="flex gap-3 md:grid md:gap-4">
                    <div class="w-[60px] md:w-full">
                      <AppInput
                        :mode="mode"
                        v-model="item.qty"
                        type="number"
                        min="0"
                        classes="text-center md:text-left"
                        placeholder="Qty"
                        :hasLabel="false"
                      />
                    </div>
                    <div class="w-[105px] md:w-full">
                      <div class="relative flex items-center">
                        <AppInput
                          :mode="mode"
                          v-model="item.price"
                          placeholder="Price"
                          maxlength="32"
                          :hasLabel="false"
                        />
                        <span
                          class="absolute right-4 z-10 flex h-6 w-6 items-center justify-center rounded bg-primary-1 text-sm font-medium text-black-100"
                          >$</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-6">
              <div class="grid">
                <AppButton title="Save" @action="updateListing" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </AppCard>
  </AuthLayout>
</template>

<script>
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useCountriesStore } from "@S/countries";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import AuthLayout from "@A/AuthLayout.vue";
import AppButton from "@C/Common/AppButton.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppLabel from "@C/Form/AppLabel.vue";
import AppCheckbox from "@C/Form/AppCheckbox.vue";

export default {
  name: "ListingEdit",
  components: {
    AuthLayout,
    AppButton,
    AppCard,
    AppSelect,
    AppInput,
    AppLabel,
    AppCheckbox,
  },
  data() {
    return {
      id: "",
      mfrs: [],
      listing: {
        mfr_id: "",
        buy_sell: "B",
        location: "",
        mfr: "",
        researching: false,
        need_by: new Date().toISOString().split("T")[0],
        subject: "",
        items: [
          {
            machine: "",
            model: "",
            comments: "",
            qty: "",
            price: "",
          },
        ],
      },
      mailqueue: {
        recipient_list: "",
      },
    };
  },
  props: {
    mode: String,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const countriesStore = useCountriesStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      countriesStore,
      handleError,
    };
  },
  created() {
    this.id = this.$route.params.id;
  },
  mounted() {
    this.initializePage();
  },
  methods: {
    async getListing() {
      try {
        const { data } = await ApiRequest().get(`/listing/${this.id}`);
        if (data) {
          this.listing = {
            mfr_id: data.listing.mfr_id,
            buy_sell: data.listing.buy_sell,
            location: data.listing.location,
            mfr: data.listing.mfr,
            researching: data.listing.researching,
            need_by: new Date(data.listing.need_by).toISOString().split("T")[0],
            subject: data.listing.subject || "",
            items: [
              {
                listing_id: data.listing.listing_id,
                machine: data.listing.machine,
                model: data.listing.model,
                comments: data.listing.comments,
                qty: data.listing.qty,
                price: data.listing.price,
              },
            ],
          };
          this.mailqueue = {
            recipient_list: "ALL" + data.listing.buy_sell + data.listing.mfr_id,
          };
        }
      } catch (e) {
        this.handleError(e);
      }
    },
    async updateListing() {
      this.loaderStore.setIsLoading(true);
      this.listing.mfr = this.mfrs && this.mfrs.length > 0 ? this.mfrs[0].mfr.mfr : "";
      const payload = {
        listing: this.listing,
        mailqueue: {
          recipient_list: "ALL" + this.listing.buy_sell + this.listing.mfr_id,
        },
      };
      try {
        const { data } = await ApiRequest().post(`/listing`, payload);
        if (data) {
          this.toastStore.show(
            "Listing updated and relisted successfully!",
            "success",
          );
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async getMfrs() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/mfr_summary`);
        if (data) {
          this.mfrs = data
            .filter((item) => item.mfr.mfr && item.mfr.mfr.trim() !== "")
            .sort((a, b) => a.mfr.mfr.localeCompare(b.mfr.mfr));
          // this.listing.mfr_id =
          //   this.mfrs && this.mfrs.length > 0 ? this.mfrs[0].mfr.mfr_id : "";
          // this.listing.mfr =
          //   this.mfrs && this.mfrs.length > 0 ? this.mfrs[0].mfr.mfr : "";
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async initializePage() {
      await Promise.all([this.getMfrs(), this.getListing()]);
    },
  },
  computed: {
    loggedInUser() {
      return this.userStore.user;
    },
    contact() {
      return this.loggedInUser.contact;
    },
    member() {
      return this.loggedInUser.member;
    },
    countries() {
      return this.countriesStore.countries;
    },
  },
};
</script>
